import { useId, type ReactNode } from 'react';
import { translateServerMessage, useT } from '../../i18n';
import { cn } from '../../lib/cn';

export interface FieldProps {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean;
  className?: string;
  /** Receives the generated id so the control is properly labelled. */
  children: (props: { id: string; invalid: boolean; describedBy?: string }) => ReactNode;
}

/** Label + control + error/hint wiring, so every form field is accessible. */
export const Field = ({ label, error, hint, required, className, children }: FieldProps) => {
  const { t } = useT();
  const id = useId();
  const messageId = `${id}-message`;
  // Validation messages come from the shared Zod schemas in English; the same
  // table that localises API errors localises them here.
  const localisedError = error ? (translateServerMessage(error, t) ?? error) : undefined;
  const message = localisedError ?? hint;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-[13px] font-medium text-ink">
        {label}
        {required ? <span className="ml-0.5 text-fail">*</span> : null}
      </label>
      {children({
        id,
        invalid: Boolean(error),
        ...(message ? { describedBy: messageId } : {}),
      })}
      {message ? (
        <p
          id={messageId}
          className={cn('text-[12px]', error ? 'text-fail' : 'text-ink-subtle')}
          role={error ? 'alert' : undefined}
        >
          {message}
        </p>
      ) : null}
    </div>
  );
};
